import { serviceOfferingTakedownReasons } from '@civitai/shared/service-offering-takedown';
type UnpublishReasonDetail = {
  optionLabel: string;
  notificationMessage: string;
  /** `policy` reasons are a rules violation — Terms of Service or content guidelines. `quality` reasons are not. */
  type: 'policy' | 'quality';
};

export const unpublishReasons = {
  'no-posts': {
    optionLabel: 'Missing images',
    notificationMessage:
      'Your model does not include example images, or the provided images were removed for violating our Terms of Service. Please upload new example images that adhere to our guidelines.',
    type: 'quality',
  },
  'no-versions': {
    optionLabel: 'Missing version',
    notificationMessage:
      'Your model currently has no published versions. Please publish a version to make it available to site users.',
    type: 'quality',
  },
  'no-files': {
    optionLabel: 'Missing files',
    notificationMessage:
      'Your model does not include any valid files. Please ensure you have uploaded a valid resource.',
    type: 'quality',
  },
  'mature-real-person': {
    optionLabel: 'Depiction of real-person likeness',
    notificationMessage:
      'Resources designed to depict real people are prohibited under our content guidelines.',
    type: 'policy',
  },
  'mature-underage': {
    optionLabel: 'Minors displayed in a mature context',
    notificationMessage:
      'Resources designed to depict minors in sexualized or suggestive clothing, poses, or situations are prohibited under our content guidelines.',
    type: 'policy',
  },
  'photo-real-underage': {
    optionLabel: 'Photorealistic depiction of a minor',
    notificationMessage:
      'Resources designed to depict photorealistic minors are prohibited under our content guidelines.',
    type: 'policy',
  },
  'hate-speech': {
    optionLabel: 'Promotion of hate-speech or targeted attack',
    notificationMessage:
      'Content that attacks, harasses, or discriminates against an individual or group based on their race, ethnicity, nationality, religion, gender, sexual orientation, disability, or any other characteristic is prohibited under our content guidelines.',
    type: 'policy',
  },
  scat: {
    optionLabel: 'Depiction of scat, fecal matter, urine, or diapers',
    notificationMessage:
      'Resources intended to depict feces ("scat"), urine, or diapers are prohibited under our content guidelines.',
    type: 'policy',
  },
  violence: {
    optionLabel: 'Prohibited violent activities',
    notificationMessage:
      'Resources intended to depict graphic violence, death, extreme gore, or other forms of extreme content are prohibited under our content guidelines.',
    type: 'policy',
  },
  'self-harm': {
    optionLabel: 'Self-harm, body image disorders, or dangerous behavior',
    notificationMessage:
      'Resources intended to depict or promote self-harm, suicide, cutting, mutilation, disordered eating such as anorexia or bulimia, or other dangerous behavior are prohibited under our content guidelines.',
    type: 'policy',
  },
  beastiality: {
    optionLabel: 'Depiction of "bestiality"',
    notificationMessage:
      'Resources intended to depict sexual activity with animals or any content involving animals in a sexual context are prohibited under our content guidelines.',
    type: 'policy',
  },
  nudify: {
    optionLabel: 'Nudifying tool',
    notificationMessage:
      'AI workflows, models, or tools designed with the intention of removing clothing or otherwise nudifying individuals are prohibited under our content guidelines.',
    type: 'policy',
  },
  'non-generated-image': {
    optionLabel: 'Images are not generated by the resource',
    notificationMessage:
      'Example images for a resource must either be generated by the resource itself or serve as an aid in demonstrating its use.',
    type: 'quality',
  },
  'unintenteded-use': {
    optionLabel: 'Unintended site use',
    notificationMessage:
      'Content uploaded to Civitai as a model must be a valid resource intended for use in content generation. Models uploaded solely for testing purposes are prohibited.',
    type: 'policy',
  },
  'insufficient-description': {
    optionLabel: 'Insufficient description',
    notificationMessage:
      'Your resource lacks a sufficient description. Please provide a clear and descriptive title along with a detailed resource description. Please tag your model appropriately, and consider including usage tips and any additional information that will help users utilize your content.',
    type: 'quality',
  },
  duplicate: {
    optionLabel: 'Duplicate upload',
    notificationMessage:
      'Your resource matches a previously uploaded resource and is considered a duplicate. Uploading duplicate files is prohibited under our content guidelines.',
    type: 'quality',
  },
  spam: {
    optionLabel: 'Spam or advertorial content',
    notificationMessage:
      'Spam, or advertisements disguised as resources, are strictly prohibited under our Terms of Service.',
    type: 'policy',
  },
  other: {
    optionLabel: 'Other',
    notificationMessage: '',
    type: 'policy',
  },
} as const satisfies Record<string, UnpublishReasonDetail>;

export type UnpublishReason = keyof typeof unpublishReasons;

/**
 * The reasons a moderator may unpublish an ARTICLE, with copy addressed to an article's author.
 *
 * Deliberately a subset. The omitted reasons describe an uploaded resource rather than a piece of
 * writing — its files, its versions, where its example images came from, or what it does when it is
 * run. `no-posts` is the exception: never offered, but stored on live articles, so
 * `legacyArticleUnpublishReasons` below carries article wording for it.
 *
 * The declaration order is the picker's order: policy reasons, then the two quality ones, then `other`.
 * Grouping them means a misclick between neighbours can only swap two reasons that say roughly the same
 * thing to the author, rather than turning "your write-up is thin" into an accusation.
 */
export const articleUnpublishReasons = {
  'mature-real-person': {
    optionLabel: 'Depiction of real-person likeness',
    notificationMessage:
      'Articles depicting real people in a mature context are prohibited under our content guidelines.',
    type: 'policy',
  },
  'mature-underage': {
    optionLabel: 'Minors displayed in a mature context',
    notificationMessage:
      'Articles depicting minors in sexualized or suggestive clothing, poses, or situations are prohibited under our content guidelines.',
    type: 'policy',
  },
  'photo-real-underage': {
    optionLabel: 'Photorealistic depiction of a minor',
    notificationMessage:
      'Articles depicting photorealistic minors are prohibited under our content guidelines.',
    type: 'policy',
  },
  'hate-speech': {
    optionLabel: 'Promotion of hate-speech or targeted attack',
    notificationMessage:
      'Content that attacks, harasses, or discriminates against an individual or group based on their race, ethnicity, nationality, religion, gender, sexual orientation, disability, or any other characteristic is prohibited under our content guidelines.',
    type: 'policy',
  },
  scat: {
    optionLabel: 'Depiction of scat, fecal matter, urine, or diapers',
    notificationMessage:
      'Articles intended to depict feces ("scat"), urine, or diapers are prohibited under our content guidelines.',
    type: 'policy',
  },
  violence: {
    optionLabel: 'Prohibited violent activities',
    notificationMessage:
      'Articles intended to depict graphic violence, death, extreme gore, or other forms of extreme content are prohibited under our content guidelines.',
    type: 'policy',
  },
  'self-harm': {
    optionLabel: 'Self-harm, body image disorders, or dangerous behavior',
    notificationMessage:
      'Articles intended to depict or promote self-harm, suicide, cutting, mutilation, disordered eating such as anorexia or bulimia, or other dangerous behavior are prohibited under our content guidelines.',
    type: 'policy',
  },
  beastiality: {
    optionLabel: 'Depiction of "bestiality"',
    notificationMessage:
      'Articles intended to depict sexual activity with animals or any content involving animals in a sexual context are prohibited under our content guidelines.',
    type: 'policy',
  },
  spam: {
    optionLabel: 'Spam or advertorial content',
    notificationMessage:
      'Spam, or advertisements disguised as articles, are strictly prohibited under our Terms of Service.',
    type: 'policy',
  },
  'unintenteded-use': {
    optionLabel: 'Unintended site use',
    notificationMessage:
      'Articles must be genuine written content intended for the community. Articles published solely for testing are prohibited.',
    type: 'policy',
  },
  'insufficient-description': {
    optionLabel: 'Insufficient content',
    notificationMessage:
      'Your article does not contain enough content to be published. Please give it a clear, descriptive title and a detailed body, and tag it appropriately so readers can find it.',
    type: 'quality',
  },
  duplicate: {
    optionLabel: 'Duplicate article',
    notificationMessage:
      'Your article repeats one that has already been published. Please update the original rather than publishing another copy of it.',
    type: 'quality',
  },
  other: {
    optionLabel: 'Other',
    notificationMessage: '',
    type: 'policy',
  },
} as const satisfies Partial<Record<UnpublishReason, UnpublishReasonDetail>>;

export type ArticleUnpublishReason = keyof typeof articleUnpublishReasons;

/**
 * Reason keys stored on live articles that the picker no longer offers, worded for an article's
 * author. Resolving these against `unpublishReasons` instead is what put "Your model does not
 * include example images" on an article page in production.
 */
export const legacyArticleUnpublishReasons = {
  'no-posts': {
    optionLabel: 'Missing images',
    notificationMessage:
      "Your article's images are missing, or were removed for violating our Terms of Service. Please add images that adhere to our guidelines.",
    type: 'quality',
  },
} as const satisfies Partial<Record<UnpublishReason, UnpublishReasonDetail>>;

/**
 * `Object.hasOwn`, not a bare index: these are plain objects, so a reason like 'constructor' or
 * 'toString' resolves off `Object.prototype` and `??` does not reject the function it returns.
 */
function lookupReason(
  map: Record<string, UnpublishReasonDetail>,
  reason: string
): UnpublishReasonDetail | undefined {
  return Object.hasOwn(map, reason) ? map[reason] : undefined;
}

export function getUnpublishReason(reason: string): UnpublishReasonDetail | undefined {
  return lookupReason(unpublishReasons, reason);
}

// One list for both places that take an offering down: here and the moderator app's report queue.
export {
  serviceOfferingTakedownReasons,
  type ServiceOfferingTakedownReason,
} from '@civitai/shared/service-offering-takedown';

export function getServiceOfferingTakedownReason(
  reason: string
): UnpublishReasonDetail | undefined {
  return lookupReason(serviceOfferingTakedownReasons, reason);
}

/** Never falls back to `unpublishReasons` — everything it returns is safe to show an article's author. */
export function getArticleUnpublishReason(reason: string): UnpublishReasonDetail | undefined {
  return (
    lookupReason(articleUnpublishReasons, reason) ??
    lookupReason(legacyArticleUnpublishReasons, reason)
  );
}
